import { observer } from 'mobx-react-lite';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography } from '@workspace/ui/components/Typography';
import { useBreakpoint } from '@workspace/ui/hooks/useBreakpoint';
import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import { formatVolume } from '~/components/ProductCard/utils';
import { useProductCardV3 } from '~/components/ProductCardV3/hooks/useProductCardV3';
import {
  AddToCartSection,
  ProductBadges,
  ProductImage,
  ProductInfo,
} from '~/components/ProductCardV3/shared';
import type { ProductCardVariantProps } from '~/components/ProductCardV3/types';
import { useProductSnippetAnalytics } from '~/components/ProductListAnalytics';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';

import styles from './ProductCardS.module.scss';

/**
 * ProductCardS - Small size product card (96px mobile, 164px web)
 * Compact card for recommendations and sliders
 */
const ProductCardS = ({
  product,
  index,
  className,
  hideButton = false,
  hideBadges = false,
  productState,
  disabled = false,
  transparent = false,
}: ProductCardVariantProps) => {
  const { t } = useTranslation();
  const { handleClickProduct } = useCategoryAnalytics();
  const { isMobile } = useBreakpoint();

  const {
    currentItem,
    offerSku,
    limit,
    limitReachedText,
    image,
    oldPrice,
    currentPrice,
    cartItemsCount,
    isFavorite,
    isOutOfStock,
    badges,
    multiBuyInfo,
    productHasManyVariants,
    isBundle,
    toggleFavorite,
    changeCount,
    openSelectSKUModal,
  } = useProductCardV3(
    productState?.data ?? product,
    productState?.selectedOffer,
    's',
    hideBadges,
  );

  const { snippetRef, trackSnippetClick } = useProductSnippetAnalytics(
    currentItem,
    index,
  );

  const handleProductClick = useCallback(() => {
    trackSnippetClick();
    handleClickProduct(product);
  }, [product, handleClickProduct, trackSnippetClick]);

  // Catalog badges are deliberately absent: this size shows a single system
  // badge and nothing more, which is the rule agreed for the small cards.
  const visibleBadges = badges.filter((badge) =>
    company.match({
      mcosmetic: badge.type === 'discount',
      default: badge.type === 'suggested',
    }),
  );

  const volume = formatVolume(currentItem.properties);
  const hasDiscount = oldPrice && oldPrice !== currentPrice;

  const PriceComponent = isMobile
    ? Typography.Text50Semibold
    : Typography.Text400Semibold;

  return (
    <div
      ref={snippetRef}
      className={cn(
        styles.card,
        disabled && styles.disabled,
        transparent && 'bg-transparent!',
        className,
      )}
      data-company={company.name}
    >
      <div className={styles.imageWrapper}>
        <ProductImage
          src={image}
          alt={currentItem.name}
          size="s"
          badges={
            visibleBadges.length ? (
              <ProductBadges
                variant={company.match({
                  mcosmetic: transparent ? 'transparent' : 'brandOutline',
                  default: 'primary',
                })}
                badges={visibleBadges}
                size={company.match({
                  mcosmetic: isMobile ? 'xs' : 'm',
                  default: 's',
                })}
              />
            ) : null
          }
          isFavorite={isFavorite}
          onToggleFavorite={toggleFavorite}
          productSlug={product.slug}
          offerSku={offerSku}
          onProductClick={handleProductClick}
          cartItemsCount={cartItemsCount}
          isOutOfStock={isOutOfStock}
          transparent={transparent}
        />

        {!hideButton && (
          <AddToCartSection
            currentPrice={currentPrice}
            oldPrice={hasDiscount ? oldPrice : undefined}
            cartItemsCount={cartItemsCount}
            limit={limit}
            limitReachedText={limitReachedText}
            size={company.match({
              mcosmetic: 'xs',
              default: 's',
            })}
            onChangeCount={changeCount}
            className={styles.addToCartSection}
            hidePrice
            productId={productState?.data?.id ?? product?.id ?? 0}
            sku={productState?.selectedOffer?.sku ?? currentItem.sku ?? ''}
            isOutOfStock={isOutOfStock}
            productSlug={product.slug}
            productHasManyVariants={productHasManyVariants}
            isBundle={isBundle}
            onOpenSelectSKUModal={openSelectSKUModal}
            transparent={transparent}
          />
        )}
      </div>

      <div className={styles.infoWrapper}>
        <ProductInfo
          onProductClick={handleProductClick}
          name={currentItem.name}
          volume={volume}
          rating={currentItem.ratingAverage}
          reviewsCount={currentItem.ratingMarksCount}
          isRatingShown={false}
          size="s"
          productSlug={product.slug}
          currentSku={offerSku}
          transparent={transparent}
        />

        {!isOutOfStock && (
          <div className={styles.priceWrapper}>
            <PriceComponent
              as="span"
              className={
                transparent ? 'text-text-inverse-primary' : 'text-text-primary'
              }
            >
              {currentPrice}
            </PriceComponent>
            {hasDiscount && (
              <span
                className={cn(
                  styles.oldPrice,
                  transparent && 'text-graphic-transparent-white-55!',
                )}
              >
                {oldPrice}
              </span>
            )}

            {multiBuyInfo && (
              <div className={styles.multiBuyInfo}>
                {cartItemsCount === 0
                  ? t('multibuyOfferNoCartValue', {
                      count: multiBuyInfo.remainingCount,
                      value: multiBuyInfo.discountValue,
                    })
                  : multiBuyInfo.remainingCount &&
                      multiBuyInfo.nextDiscountPercent
                    ? t('multibuyOfferWithCartValue', {
                        count: Math.max(0, multiBuyInfo.remainingCount),
                        value: multiBuyInfo.nextDiscountValue,
                      })
                    : null}
              </div>
            )}
          </div>
        )}
      </div>

      {disabled && <div className={styles.disabledOverlay} />}
    </div>
  );
};

export default observer(ProductCardS);
